import type React from "react";
import { ChevronLeft, ChevronRight, Eye, EyeOff, GripVertical } from "lucide-react";

import { cn } from "@/lib/shadcn/utils";
import type { TileDef } from "./types";

export interface EditableTileProps {
    tile: TileDef;
    title: string;
    editMode: boolean;
    hidden: boolean;
    isFirst: boolean;
    isLast: boolean;
    onToggleHide: (id: string) => void;
    onMovePrev: (id: string) => void;
    onMoveNext: (id: string) => void;
    children: React.ReactNode;
}

// Shared focus ring, matching stats-card's info button.
const chromeButton =
    "inline-flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
    "disabled:opacity-40 disabled:pointer-events-none";

const EditableTile: React.FC<EditableTileProps> = ({
    tile,
    title,
    editMode,
    hidden,
    isFirst,
    isLast,
    onToggleHide,
    onMovePrev,
    onMoveNext,
    children,
}) => {
    // Read mode: no chrome. A hidden tile is simply not rendered. A visible tile
    // still carries the stable `tile-<id>` wrapper (+ type/hidden data attrs) so the
    // contract test surface holds in both modes.
    if (!editMode) {
        if (hidden) {
            return null;
        }
        return (
            <div data-testid={`tile-${tile.id}`} data-tile-type={tile.type} data-hidden="false" className="h-full w-full">
                {children}
            </div>
        );
    }

    // Edit mode: render the tile (even when hidden, dimmed, so it can be un-hidden)
    // and overlay the edit chrome.
    return (
        <div
            data-testid={`tile-${tile.id}`}
            data-tile-type={tile.type}
            data-hidden={hidden ? "true" : "false"}
            className={cn("relative h-full w-full", hidden && "opacity-40")}
        >
            <div className="absolute right-1 top-1 z-10 flex items-center gap-0.5 rounded-md bg-background/80 p-0.5 shadow-sm backdrop-blur">
                <button
                    type="button"
                    data-testid={`tile-drag-handle-${tile.id}`}
                    aria-label={`Drag to reorder ${title}`}
                    className={cn(chromeButton, "tile-drag-handle cursor-grab p-1")}
                >
                    <GripVertical className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    data-testid={`tile-move-prev-${tile.id}`}
                    aria-label={`Reorder ${title} earlier`}
                    disabled={isFirst}
                    onClick={() => onMovePrev(tile.id)}
                    className={cn(chromeButton, "p-1")}
                >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    data-testid={`tile-move-next-${tile.id}`}
                    aria-label={`Reorder ${title} later`}
                    disabled={isLast}
                    onClick={() => onMoveNext(tile.id)}
                    className={cn(chromeButton, "p-1")}
                >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    data-testid={`tile-visibility-${tile.id}`}
                    aria-pressed={hidden}
                    aria-label={hidden ? `Show ${title}` : `Hide ${title}`}
                    onClick={() => onToggleHide(tile.id)}
                    className={cn(chromeButton, "p-1")}
                >
                    {hidden ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                </button>
            </div>

            {hidden && (
                <div className="pointer-events-none absolute left-1 top-1 z-10 rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                    Hidden
                </div>
            )}

            {children}
        </div>
    );
};

export default EditableTile;
